import React, { useState, useEffect, useRef } from 'react';
import { multiFactor, TotpMultiFactorGenerator, EmailAuthProvider, reauthenticateWithCredential, sendEmailVerification } from 'firebase/auth';
import QRCode from 'qrcode';
import { auth } from '../shared/api/firebase/config'; 
import { FIREBASE_CONFIG } from '../config/Config';
import { saveBackupCodes } from '../services/MfaSetupRepository';

// UTILITÁRIO: Hashing seguro no cliente (Web Crypto API - HTTPS Required)
const hashBackupCode = async (code) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const MFASetupModal = ({ user, onClose }) => {
    // STATE MACHINE
    const [step, setStep] = useState('INIT'); // INIT, LOADING, SETUP, VERIFYING, BACKUP, SUCCESS
    const [setupMethod, setSetupMethod] = useState('QR');
    
    // DADOS SENSÍVEIS (Voláteis)
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [totpSecret, setTotpSecret] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
    const [secretKeyText, setSecretKeyText] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    
    // UI & SAFETY
    const [error, setError] = useState('');
    const [emailUnverified, setEmailUnverified] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    
    // REFS PARA CLEANUP (Memória)
    const abortControllerRef = useRef(null);
    const clipboardTimerRef = useRef(null);

    // CONSTANTES DE INFRA
        // TRAVA DE SAÍDA (Anti-Lockout)
    // Impede fechar o modal se o MFA já estiver ativo mas os códigos não foram baixados
    const isTrapped = step === 'BACKUP' || step === 'VERIFYING';

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        
        // Anti-Fechamento Acidental do Navegador
        const handleBeforeUnload = (e) => {
            if (isTrapped) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
        };
    }, [isTrapped]);

    useEffect(() => {
        let cancelled = false;

        const generateQr = async () => {
            if (!qrCodeUrl) {
                setQrCodeDataUrl('');
                return;
            }

            try {
                const dataUrl = await QRCode.toDataURL(qrCodeUrl, { margin: 1, width: 220 });
                if (!cancelled) setQrCodeDataUrl(dataUrl);
            } catch (e) {
                console.error('QR generation error:', e);
                if (!cancelled) setQrCodeDataUrl('');
            }
        };

        generateQr();
        return () => {
            cancelled = true;
        };
    }, [qrCodeUrl]);

    // 1. RE-AUTENTICAÇÃO & GERAÇÃO DE SEGREDO
    const handleReauth = async () => {
        setStep('LOADING');
        setError('');
        setEmailUnverified(false);
        
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        try {
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
            
            if (abortControllerRef.current.signal.aborted) return;

            const session = await multiFactor(user).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            
            setTotpSecret(secret);
            setSecretKeyText(secret.secretKey);
            
            // ATUALIZAÇÃO: Nome Exibido no App Autenticador
            setQrCodeUrl(secret.generateQrCodeUrl(user.email, "Rodrigo Demeterco Advocacia"));
            
            setSetupMethod(isMobile ? 'MANUAL' : 'QR');
            setStep('SETUP');

        } catch (e) {
            if (abortControllerRef.current?.signal.aborted) return;
            console.error("MFA Error:", e);
            setStep('INIT');
            
            if (e.code === 'auth/wrong-password') {
                setError("Senha incorreta.");
            } else if (e.code === 'auth/unverified-email') {
                setError("E-mail não verificado. Verifique sua caixa de entrada.");
                setEmailUnverified(true);
            } else if (e.code === 'auth/maximum-second-factor-count-exceeded') {
                setError("Limite de dispositivos atingido. Remova um antigo nas configurações.");
            } else {
                setError(`Erro: ${e.message}`);
            }
        }
    };

    const handleSendVerification = async () => {
        try {
            const isLocalQaHost =
                typeof window !== 'undefined' &&
                (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');
            if (isLocalQaHost) {
                setError("Modo QA: envio de verificação por e-mail desativado no localhost.");
                return;
            }
            await sendEmailVerification(user);
            alert(`E-mail enviado para ${user.email}.\nVerifique também a caixa de SPAM.`);
            setError("E-mail enviado! Aguardando verificação...");
        } catch (e) {
            setError("Erro ao enviar: " + e.message);
        }
    };

    // 2. GERAÇÃO DE CÓDIGOS EM MEMÓRIA (STEP CRÍTICO)
    const generateCodesInMemory = async () => {
        const codes = [];
        const hashedCodes = [];
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Base32 Safe

        for (let i = 0; i < 10; i++) {
            let code = '';
            const randomValues = new Uint32Array(8);
            window.crypto.getRandomValues(randomValues);
            for (let j = 0; j < 8; j++) {
                code += chars[randomValues[j] % chars.length];
            }
            const formatted = `${code.slice(0,4)}-${code.slice(4)}`;
            codes.push(formatted);
            const hash = await hashBackupCode(formatted);
            hashedCodes.push({ hash, used: false });
        }
        return { plain: codes, hashed: hashedCodes };
    };

    // 3. ATIVAÇÃO BLINDADA (SAFE-FAIL)
    const handleActivate = async () => {
        setStep('VERIFYING');
        setError('');
        
        try {
            // A. Validação TOTP
            const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                totpSecret, 
                verificationCode
            );
            
            // B. Geração Antecipada dos Códigos (Memória)
            // Se falhar algo depois, já temos eles para mostrar
            const generatedCodes = await generateCodesInMemory();
            setBackupCodes(generatedCodes.plain); 

            // C. Ativação no Firebase (PONTO DE NÃO RETORNO)
            await multiFactor(user).enroll(multiFactorAssertion, "Google Authenticator");
            
            // D. Persistência no Firestore
            try {
                await saveBackupCodes(user.uid, generatedCodes.hashed);
                // Sucesso Total
                setStep('BACKUP');
                await user.reload(); 

            } catch (saveError) {
                console.error("Erro crítico ao salvar backup:", saveError);
                // E. MODO DE EMERGÊNCIA
                // O MFA está ativo, mas os códigos não foram salvos no banco.
                // Mostramos os códigos da memória para o usuário anotar manualmente.
                alert("⚠️ ALERTA DE SEGURANÇA:\nO MFA foi ativado, mas houve um erro ao salvar seus códigos no servidor.\n\nVocê verá os códigos na próxima tela. SALVE-OS IMEDIATAMENTE, pois eles não estão no banco de dados.");
                setStep('BACKUP'); // Força a tela de backup com os dados da memória
            }
            
        } catch (e) {
            console.error(e);
            setStep('SETUP');
            if (e.code === 'auth/invalid-verification-code') {
                setError("Código inválido. Verifique o relógio do celular.");
            } else if (e.code === 'auth/code-expired') {
                setError("Código expirado. Digite o novo código do app.");
            } else {
                setError("Erro na ativação: " + e.message);
            }
        }
    };

    // UTILS
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert("Copiado! Área de transferência será limpa em 60s.");
        if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
        clipboardTimerRef.current = setTimeout(() => { navigator.clipboard.writeText(''); }, 60000);
    };

    const downloadBackupCodes = () => {
        const element = document.createElement("a");
        const file = new Blob([
            `CÓDIGOS DE RECUPERAÇÃO - RDA ADVOCACIA\n\nGuarde este arquivo em local OFFLINE e SEGURO.\nSe perder o celular, estes códigos são sua ÚNICA forma de entrar.\n\n${backupCodes.join('\n')}`
        ], {type: 'text/plain'});
        element.href = URL.createObjectURL(file);
        element.download = "RDA_Codigos_Recuperacao.txt";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        setHasDownloaded(true);
    };

    const isProcessing = step === 'VERIFYING';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in-up">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-indigo-600 relative">
                
                {/* HEADER */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                        <i className="fas fa-shield-alt text-indigo-600"></i> Blindagem de Conta
                    </h3>
                    {!isTrapped && (
                        <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500"></i></button>
                    )}
                </div>

                <div className="p-6">
                    {/* LOADING */}
                    {step === 'LOADING' && (
                        <div className="text-center py-8">
                            <i className="fas fa-circle-notch fa-spin text-3xl text-indigo-600 mb-4"></i>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Processando...</p>
                        </div>
                    )}

                    {/* 1. SENHA */}
                    {step === 'INIT' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-lock text-2xl text-indigo-600"></i>
                            </div>
                            <h4 className="font-bold text-gray-800 mb-2">Autenticação em 2 Fatores</h4>
                            <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                                Confirme sua senha atual para configurar o <strong>Google Authenticator</strong>.
                            </p>
                            <input type="password" className="w-full p-3 border rounded mb-4 text-sm focus:border-indigo-600 outline-none" placeholder="Sua senha atual" value={password} onChange={e => setPassword(e.target.value)} autoFocus />
                            {error && (
                                <div className="bg-red-50 p-3 rounded mb-3 border border-red-100">
                                    <p className="text-red-600 text-[10px] font-bold text-left mb-2">{error}</p>
                                    {emailUnverified && <button onClick={handleSendVerification} className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] rounded shadow-sm">REENVIAR EMAIL</button>}
                                </div>
                            )}
                            <button onClick={handleReauth} disabled={!password} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded shadow">INICIAR CONFIGURAÇÃO</button>
                        </div>
                    )}

                    {/* 2. QR CODE / MANUAL */}
                    {(step === 'SETUP' || step === 'VERIFYING') && (
                        <div className={`space-y-4 ${isProcessing ? 'opacity-70 pointer-events-none' : ''}`}>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button onClick={() => setSetupMethod('QR')} className={`flex-1 py-1.5 text-[10px] font-bold rounded transition-all ${setupMethod === 'QR' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}>QR CODE</button>
                                <button onClick={() => setSetupMethod('MANUAL')} className={`flex-1 py-1.5 text-[10px] font-bold rounded transition-all ${setupMethod === 'MANUAL' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}>MANUAL</button>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center min-h-[200px] flex flex-col items-center justify-center">
                                {setupMethod === 'QR' ? (
                                    <>
                                        <div className="bg-white p-2 border rounded shadow-sm mb-2">
                                            {qrCodeDataUrl && (
                                                <img src={qrCodeDataUrl} alt="QR Code" className="w-[140px] h-[140px]" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">Abra o Google Authenticator e aponte a câmera</p>
                                    </>
                                ) : (
                                    <div className="w-full">
                                        <p className="text-[10px] text-gray-500 mb-2 uppercase font-bold">Chave de Configuração</p>
                                        <div className="bg-white border border-dashed border-gray-300 p-3 rounded font-mono text-sm text-gray-700 break-all mb-3 select-all tracking-wider text-center">
                                            {secretKeyText?.match(/.{1,4}/g)?.join(' ')}
                                        </div>
                                        <button onClick={() => copyToClipboard(secretKeyText)} className="text-xs font-bold text-indigo-600 hover:bg-indigo-50 px-3 py-1.5 rounded border border-indigo-200 flex items-center justify-center gap-2 w-full mb-2"><i className="far fa-copy"></i> COPIAR CHAVE</button>
                                    </div>
                                )}
                            </div>

                            <div className="pt-2 border-t border-gray-100">
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Código Gerado (6 Dígitos)</label>
                                <input className="w-full p-3 text-center text-xl font-mono tracking-[0.5em] border rounded mb-2 uppercase focus:border-indigo-600 outline-none" placeholder="000000" maxLength={6} autoComplete="one-time-code" inputMode="numeric" value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g,''))} disabled={isProcessing} />
                                {error && <p className="text-red-500 text-[10px] font-bold mb-2 text-center">{error}</p>}
                                <button onClick={handleActivate} disabled={verificationCode.length !== 6 || isProcessing} className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold text-xs rounded shadow flex justify-center items-center gap-2">
                                    {isProcessing ? <><i className="fas fa-circle-notch fa-spin"></i> BLINDANDO CONTA...</> : 'ATIVAR PROTEÇÃO'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 3. BACKUP CODES (CRÍTICO) */}
                    {step === 'BACKUP' && (
                        <div className="text-center animate-fade-in-up">
                            <div className="mb-4">
                                <i className="fas fa-life-ring text-4xl text-orange-500 mb-2 animate-bounce"></i>
                                <h4 className="font-bold text-gray-800 text-lg">Salve seus Códigos!</h4>
                                <p className="text-xs text-gray-600 mt-1 px-4">
                                    O MFA está <strong>ATIVO</strong>. Salve estes códigos em local OFFLINE. Se perder o celular, eles são a única forma de entrar.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-800 p-3 rounded-lg border border-gray-700 font-mono text-xs text-green-400 shadow-inner max-h-32 overflow-y-auto custom-scrollbar">
                                {backupCodes.map((code, i) => (
                                    <div key={i} className="bg-black/30 p-1 rounded text-center tracking-widest">{code}</div>
                                ))}
                            </div>

                            <button onClick={downloadBackupCodes} className={`w-full py-4 text-white font-bold text-sm rounded shadow-lg mb-3 flex items-center justify-center gap-2 transition-all transform hover:scale-105 ${hasDownloaded ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                <i className="fas fa-download"></i> {hasDownloaded ? 'BAIXAR NOVAMENTE' : 'BAIXAR CÓDIGOS (.TXT)'}
                            </button>
                            
                            <button onClick={() => setStep('SUCCESS')} disabled={!hasDownloaded} className={`w-full py-2 font-bold text-[10px] uppercase transition-colors ${hasDownloaded ? 'text-gray-500 hover:text-gray-800 cursor-pointer underline' : 'text-gray-300 cursor-not-allowed'}`}>
                                {hasDownloaded ? 'Continuar (Já salvei)' : 'Baixe para continuar'}
                            </button>
                        </div>
                    )}

                    {/* 4. SUCESSO */}
                    {step === 'SUCCESS' && (
                        <div className="text-center animate-fade-in-up">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-check text-2xl text-green-600"></i>
                            </div>
                            <h4 className="font-bold text-green-700 mb-2">Conta Blindada!</h4>
                            <p className="text-xs text-gray-500 mb-6">Autenticação de dois fatores ativa.</p>
                            <button onClick={onClose} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold text-xs rounded">FECHAR</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MFASetupModal;